import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Loader2, CheckCircle2, Send, Camera } from 'lucide-react';
import { useTheme } from '../../styles/ThemeContext';
interface AIPhotographerModalProps {
  isOpen: boolean;
  onClose: () => void;
  screenshotUrl: string;
  onEnhance: () => Promise<void>;
  onSendEmail: (email: string) => Promise<void>;
}
type Step = 'preview' | 'processing' | 'submitted';
const PROCESSING_PHRASES = ['Analyzing composition and depth of field...', 'Enhancing with editorial-grade lighting...', 'Applying Architectural Digest styling...', 'Refining textures and material details...', 'Adding professional lens effects...', 'Perfecting the final shot...'];
export function AIPhotographerModal({
  isOpen,
  onClose,
  screenshotUrl,
  onEnhance,
  onSendEmail
}: AIPhotographerModalProps) {
  const {
    isDark
  } = useTheme();
  const [step, setStep] = useState<Step>('preview');
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setStep('preview');
      setSendSuccess(false);
      setIsSending(false);
      setPhraseIndex(0);
    }
  }, [isOpen]);
  // Rotate processing phrases
  useEffect(() => {
    if (step !== 'processing') return;
    setPhraseIndex(0);
    const interval = setInterval(() => {
      setPhraseIndex(prev => (prev + 1) % PROCESSING_PHRASES.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [step]);
  const handleEnhance = async () => {
    setStep('processing');
    try {
      await onEnhance();
      // Simulate a bit of extra processing time for the fun animation
      setTimeout(() => {
        setStep('submitted');
      }, 3000);
    } catch (error) {
      console.error('Enhancement failed', error);
      // In a real app, handle error state here
      setStep('preview');
    }
  };
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSending(true);
    await onSendEmail(email);
    setIsSending(false);
    setSendSuccess(true);
  };
  return <AnimatePresence data-id="element-205">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-206">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-207" />

          {/* Modal Container */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`relative w-full max-w-[420px] rounded-3xl shadow-2xl overflow-hidden border ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-208">
            {/* Close Button */}
            {step !== 'processing' && <button onClick={onClose} className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-colors ${isDark ? 'bg-black/20 hover:bg-black/40 text-white/70 hover:text-white' : 'bg-white/50 hover:bg-white/80 text-gray-500 hover:text-gray-900'}`} data-id="element-209">
                <X className="w-5 h-5" data-id="element-210" />
              </button>}

            <div className="p-6" data-id="element-211">
              {/* Image Container */}
              <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden mb-6 bg-black/10" data-id="element-212">
                <img src={screenshotUrl} alt="Screenshot" className="w-full h-full object-cover" data-id="element-213" />

                {/* Processing Shimmer Effect */}
                {step === 'processing' && <>
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" data-id="element-214" />
                    <motion.div className="absolute top-0 bottom-0 w-1/2 bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent skew-x-[-20deg]" animate={{
                left: ['-100%', '200%']
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-215" />
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-216">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-217">
                        <Loader2 className="w-10 h-10 text-cyan-400" data-id="element-218" />
                      </motion.div>
                    </div>
                  </>}
              </div>

              {/* Content Area */}
              <div className="text-center" data-id="element-219">
                <AnimatePresence mode="wait" data-id="element-220">
                  {step === 'preview' && <motion.div key="preview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-col items-center" data-id="element-221">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-100 text-teal-700'}`} data-id="element-222">
                        <Sparkles className="w-4 h-4" data-id="element-223" />
                        <span className="text-xs font-bold uppercase tracking-wider" data-id="element-224">
                          AI Photographer
                        </span>
                      </div>
                      <p className={`text-sm mb-8 leading-relaxed ${isDark ? 'text-slate-300' : 'text-gray-600'}`} data-id="element-225">
                        Our AI will transform this into a magazine-quality
                        architectural photo and email it to you.
                      </p>
                      <div className="w-full flex flex-col gap-3" data-id="element-226">
                        <button onClick={handleEnhance} className={`w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98] ${isDark ? 'bg-cyan-500 hover:bg-cyan-400 text-white shadow-[0_0_20px_rgba(6,182,212,0.3)]' : 'bg-teal-600 hover:bg-teal-500 text-white shadow-lg shadow-teal-500/20'}`} data-id="element-227">
                          Ready to enhance
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-228">
                            <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="currentColor" data-id="element-229" />
                          </svg>
                        </button>
                        <button onClick={onClose} className={`w-full py-3 rounded-xl text-sm font-medium transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-230">
                          Retake photo
                        </button>
                      </div>
                    </motion.div>}

                  {step === 'processing' && <motion.div key="processing" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-col items-center py-4" data-id="element-231">
                      <h3 className={`text-lg font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-232">
                        Enhancing your photo...
                      </h3>
                      <div className="h-5 mb-6" data-id="element-233">
                        <AnimatePresence mode="wait" data-id="element-234">
                          <motion.p key={phraseIndex} initial={{
                      opacity: 0,
                      y: 6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -6
                    }} transition={{
                      duration: 0.3
                    }} className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-235">
                            {PROCESSING_PHRASES[phraseIndex]}
                          </motion.p>
                        </AnimatePresence>
                      </div>

                      {/* Simulated Progress Bar */}
                      <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`} data-id="element-236">
                        <motion.div className={`h-full ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} initial={{
                    width: '0%'
                  }} animate={{
                    width: '100%'
                  }} transition={{
                    duration: 3,
                    ease: 'easeInOut'
                  }} data-id="element-237" />
                      </div>
                    </motion.div>}

                  {step === 'submitted' && <motion.div key="submitted" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-col items-center" data-id="element-238">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  damping: 15,
                  stiffness: 200
                }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`} data-id="element-239">
                        <CheckCircle2 className="w-8 h-8" data-id="element-240" />
                      </motion.div>
                      <h3 className={`text-xl font-bold mb-2 flex items-center justify-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-241">
                        On its way!
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-242">
                          <rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" data-id="element-243" />
                          <path d="M2 8L12 14L22 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-244" />
                          <path d="M12 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-245" />
                          <path d="M9 4L12 2L15 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-246" />
                        </svg>
                      </h3>
                      <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-247">
                        We'll email your magazine-quality photo as soon as it's
                        ready.
                      </p>

                      {!sendSuccess ? <form onSubmit={handleSend} className="w-full mb-4" data-id="element-248">
                          <div className="relative" data-id="element-249">
                            <input type="email" placeholder="Enter your email..." value={email} onChange={e => setEmail(e.target.value)} required className={`w-full pl-4 pr-12 py-3.5 rounded-xl border focus:outline-none transition-all ${isDark ? 'bg-slate-800/50 border-white/10 text-white placeholder-slate-500 focus:border-cyan-400 focus:bg-slate-800' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500 focus:bg-white'}`} data-id="element-250" />
                            <button type="submit" disabled={isSending || !email} className={`absolute right-1.5 top-1.5 bottom-1.5 w-10 rounded-lg flex items-center justify-center transition-colors ${isDark ? 'bg-cyan-500 hover:bg-cyan-400 text-white disabled:opacity-50' : 'bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50'}`} data-id="element-251">
                              {isSending ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-252" /> : <Send className="w-4 h-4" data-id="element-253" />}
                            </button>
                          </div>
                        </form> : <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} className={`w-full p-3 rounded-xl mb-4 text-sm font-medium ${isDark ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-254">
                          Email saved! We'll notify you shortly.
                        </motion.div>}

                      <button onClick={onClose} className={`w-full py-3.5 rounded-xl font-bold transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`} data-id="element-255">
                        Done
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}